import { useMemo } from 'react';
import { useTypedQuery } from 'apiTyped/helpers/useTypedQuery';
import { useTypedMutation } from 'apiTyped/helpers/useTypedMutation';
import type {
  PosEntry,
  ActionList,
  ActivityEntry,
  ActivityKind,
  FunnelData
} from 'v2/pages/CrmSalesTracking/types';

export interface CrmPipelineFilters {
  market?: string | null;
  channel?: string | null;
  brand?: string | null;
  search?: string | null;
  status?: string[];
  productType?: string | null;
  page?: number;
  limit?: number;
}

export const useCrmPipeline = (
  filters: CrmPipelineFilters = {},
  enabled = true
) => {
  const { isLoading, data, refetch } = useTypedQuery(
    'get',
    '/v2/crm/pipeline',
    {
      params: {
        query: {
          market: filters.market ?? undefined,
          channel: filters.channel ?? undefined,
          brand: filters.brand ?? undefined,
          search: filters.search ?? undefined,
          status: filters.status?.join(',') || undefined,
          product_type: filters.productType ?? undefined,
          page: filters.page ?? undefined,
          limit: filters.limit ?? undefined
        }
      },
      queryOpts: { enabled }
    }
  );

  const mapped = useMemo(() => {
    if (!data) return null;
    const pos: PosEntry[] = (data.pos || []).map((p) => ({
      id: p.id,
      name: p.name,
      type: p.type,
      city: p.city,
      region: p.region,
      country: p.country,
      status: p.status as PosEntry['status'],
      network: p.network as PosEntry['network'],
      yourRef: p.your_ref,
      totalRefs: p.total_refs,
      competitors: p.competitors,
      lastChange: p.last_change,
      evolution: p.evolution,
      phone: p.phone ?? undefined,
      email: p.email ?? undefined,
      website: p.website ?? undefined,
      address: p.address ?? undefined,
      primaryContact: p.primary_contact ?? undefined,
      primaryContactRole: p.primary_contact_role ?? undefined,
      wineListSize: p.wine_list_size ?? undefined,
      avgPrice: p.avg_price ?? undefined,
      lastChecked: p.last_checked ?? undefined,
      stability: (p.stability ?? undefined) as PosEntry['stability'],
      stage: (p.stage ?? undefined) as PosEntry['stage'],
      shelves: p.shelves ?? undefined,
      myRefs: p.my_refs?.map((r) => ({
        wine: r.wine,
        vintage: r.vintage,
        format: r.format,
        price: r.price,
        status: r.status as 'listed' | 'delisted' | 'new'
      })),
      competitorRefs: p.competitor_refs?.map((r) => ({
        wine: r.wine,
        vintage: r.vintage,
        format: r.format,
        price: r.price,
        status: r.status as 'listed' | 'delisted' | 'new'
      })),
      activity: p.activity?.map((a) => ({
        id: a.id,
        icon: a.icon as 'note' | 'status' | 'contact' | 'email' | 'list',
        title: a.title,
        when: a.when,
        user: a.user
      })),
      history: p.history?.map((h) => ({
        date: h.date,
        method: h.method as 'online' | 'manual' | 'import',
        clientStatus: h.client_status as PosEntry['status'],
        clientRefs: h.client_refs,
        totalRefs: h.total_refs,
        notes: h.notes
      })),
      myNotes: p.my_notes ?? undefined,
      actionListIds: p.action_list_ids ?? undefined
    }));

    const actionLists: ActionList[] = (data.action_lists || []).map((al) => ({
      id: al.id,
      name: al.name,
      createdAt: al.created_at,
      createdBy: al.created_by,
      sentTo: al.sent_to,
      sentToType: al.sent_to_type as 'distributor' | 'sales-team',
      privacy: al.privacy as 'private' | 'shared',
      action: al.action,
      notes: al.notes,
      isMine: al.is_mine ?? false,
      pos: al.pos.map((p) => ({
        id: p.id,
        name: p.name,
        city: p.city,
        initialStatus: p.initial_status as PosEntry['status'],
        currentStatus: p.current_status as PosEntry['status']
      }))
    }));

    const activity: ActivityEntry[] = (data.activity || []).map((a: any) => ({
      id: a.id,
      user: a.user,
      userInitials: a.user_initials,
      kind: (a.kind || 'note') as ActivityKind,
      action: a.action,
      target: a.target,
      targetType: a.target_type ?? undefined,
      targetId: a.target_id ?? undefined,
      detail: a.detail,
      when: a.when,
      privacy: a.privacy ?? undefined,
      with_: a.with_ ?? undefined,
      posCount: a.pos_count ?? undefined,
      sentTo: a.sent_to ?? undefined,
      sentToType: a.sent_to_type ?? undefined,
      recipientCount: a.recipient_count ?? undefined,
      recipientList: a.recipient_list ?? undefined
    }));

    const funnel: FunnelData = data.funnel;

    return { pos, actionLists, activity, funnel };
  }, [data]);

  return {
    isLoading,
    pos: mapped?.pos ?? [],
    actionLists: mapped?.actionLists ?? [],
    activity: mapped?.activity ?? [],
    funnel: mapped?.funnel ?? { prospect: 0, towin: 0, active: 0, lost: 0 },
    refetch
  };
};

// ─── Action list mutations ──────────────────────────────────────────────

export interface CreateActionListInput {
  name: string;
  action?: string;
  notes?: string;
  sentTo?: string;
  sentToType?: 'distributor' | 'sales-team';
  privacy?: 'private' | 'shared';
  storeIds?: number[];
}

/** Create a new action list (POS grouping) from the Sales Tracking selection bar. */
export const useCreateActionList = () =>
  useTypedMutation('post', '/v2/crm/action-lists', {});

export const toCreateActionListBody = (input: CreateActionListInput) => ({
  name: input.name,
  action: input.action ?? '',
  notes: input.notes ?? '',
  sent_to: input.sentTo ?? '',
  sent_to_type: input.sentToType ?? 'sales-team',
  privacy: input.privacy ?? 'shared',
  store_ids: input.storeIds ?? []
});
